import { webDataNotAsked } from '@cobliteam/api-lib';
import { Location, UserRole } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { useTitle } from '@cobliteam/hooks';
import useSessionStore from '@cobliteam/hooks/src/useSession/store/useSessionStore';
import { type AppState, history, loginResetState, selectLocationPathname } from '@cobliteam/store';
import { Loading } from '@cobliteam/ui/components';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import CobliLogo from '../../assets/cobli-logo.svg?react';
import LoginMKT from '../../components/LoginMKT';
import * as styles from './styles';

const AuthLayout = ({ children }: any) => {
  useTitle('Login');
  const dispatch = useDispatch();
  const session = useSessionStore((s) => s.session);
  const pathname = useSelector((state: AppState) => selectLocationPathname(state));
  const isHome = useMemo(() => pathname === Location.LOGIN, [pathname]);

  if (session) {
    return session.user.role === UserRole.MAP_VIEW ? <Redirect to={Location.MONITORING} /> : <Redirect to={Location.HOME} />;
  }

  const handleGoBack = () => {
    if (pathname === Location.TOKEN_VALIDATION) {
      dispatch(
        loginResetState({
          forgetPasswordWebData: webDataNotAsked(),
          validateTokenWebData: webDataNotAsked(),
          token: '',
        })
      );
    }

    history.goBack();
  };

  return (
    <div className={`grid h-screen grid-cols-12 ${styles.authBackground}`}>
      {/* Noise texture overlay */}
      <div className={styles.noiseOverlay} />

      {/* Subtle grid lines for depth */}
      <div className={`${styles.gridLine} top-0 bottom-0 left-1/4 tablet:block hidden w-px`} />
      <div className={`${styles.gridLine} top-0 right-1/4 bottom-0 tablet:block hidden w-px`} />

      <section className='relative z-10 col-span-12 tablet:col-span-6 flex items-center justify-center'>
        <div className={styles.login}>
          {/* Logo */}
          <div className={styles.logoContainer}>
            <CobliLogo className='h-24 text-white opacity-90 transition-opacity duration-300 hover:opacity-100' />
          </div>

          {/* Back button */}
          {!isHome && (
            <div className={styles.previousStep}>
              <Button
                variant='ghost'
                color='default'
                size='sm'
                startIcon={<Icon name='arrow-left' />}
                onClick={handleGoBack}
                className='text-white/60 transition-colors duration-200 hover:text-white'
              >
                Voltar
              </Button>
            </div>
          )}

          {/* Form card with glassmorphism */}
          <div className={`relative ${styles.formCard}`}>
            <Suspense
              fallback={
                <div className='flex min-h-[inherit] items-center justify-center'>
                  <Loading />
                </div>
              }
            >
              {children}
            </Suspense>
          </div>

          {/* Decorative shimmer line below card */}
          <div className={styles.shimmerLine} />
        </div>
      </section>

      <section className='relative z-10 col-span-12 tablet:col-span-6 tablet:grid hidden'>
        <ErrorBoundary
          onError={(error, errorInfo) => {
            DatadogTrack.error(error, {
              component: 'AuthLayout',
              errorInfo,
            });
          }}
          fallbackRender={() => <div className='flex h-full items-center justify-center text-white/40'>Erro ao carregar a página</div>}
        >
          <LoginMKT />
        </ErrorBoundary>
      </section>
    </div>
  );
};

export default AuthLayout;
